import { getSocket } from '@/features/atendimento/socket';
import { listChannels } from '@/features/channels/api';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

/** Ref estável pra evitar re-render do zustand quando user é null. */
const EMPTY_GROUPS: number[] = [];

/**
 * Badge no Topbar que sinaliza canais com problema.
 *
 * Critério: canais QR Code com `status !== 'active'` (pending_qr, revoked,
 * inactive). Cloud API legacy é considerado sempre OK (status managed pela Meta).
 *
 * Atualização por REALTIME (não há polling): o worker-whatsmeow publica
 * `channel.updated` na room do tenant a cada transição de status — inclusive
 * a volta pra 'active', que é o que APAGA o alerta. Vide
 * `publishChannelStatus` em worker-whatsmeow/internal/handlers/connection.go.
 *
 * UI: bolinha vermelha redonda com X pulsando. Hover/click mostra mensagem
 * informativa com lista de canais problemáticos inline no texto.
 */
export function ChannelHealthBadge() {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Compartilha cache com ['channels'] (ChannelsPage, NewActiveOutboundModal,
  // Dashboard, Consulta). Quando o user deleta/desconecta/reativa canal, a
  // invalidação rola em ['channels'] e o badge reflete imediato — sem isso,
  // badge tinha sua própria query e ficava stale até o refetchInterval (30s).
  const qc = useQueryClient();
  const channelsQ = useQuery({
    queryKey: ['channels'],
    queryFn: listChannels,
    // SEM refetchInterval de propósito: o worker avisa por realtime a cada
    // transição de status do canal (`channel.updated`, publicado na room do
    // tenant). O polling de 30s custava ~6,6k req/h numa frota de 55 pra
    // detectar um evento que acontece poucas vezes por dia.
    // staleTime cobre o resto de graça: remontar a tela ou voltar pra aba
    // revalida sem nenhum timer.
    staleTime: 30_000,
  });

  // Realtime: acende/apaga o alerta na hora que o canal cai ou volta.
  // Também revalida ao (re)conectar o socket — se um evento se perdeu enquanto
  // a conexão estava caída, isso corrige sem custo em regime.
  useEffect(() => {
    const socket = getSocket();
    const resync = (): void => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
    };
    socket.on('channel.updated', resync);
    socket.on('connect', resync);
    return () => {
      socket.off('channel.updated', resync);
      socket.off('connect', resync);
    };
  }, [qc]);

  // Grupos de acesso do user logado (do payload da sessão). Ref estável.
  const accessGroupIds = useAuthStore((s) => s.user?.accessGroupIds ?? EMPTY_GROUPS);

  // Filtra QR com problema. Cloud API ignora (status managed por Meta).
  // Visibilidade por grupo de acesso: o alerta só aparece pra quem é do grupo
  // vinculado ao canal. Canal sem grupo (default_access_group_id null) aparece
  // pra todos. SEM bypass de admin (decisão de produto: admin também restrito
  // aos grupos dele).
  const problematic = useMemo(() => {
    const list = channelsQ.data ?? [];
    return list.filter(
      (c) =>
        c.channelType === 'qr_code' &&
        c.status !== 'active' &&
        (c.defaultAccessGroupId === null || accessGroupIds.includes(c.defaultAccessGroupId)),
    );
  }, [channelsQ.data, accessGroupIds]);

  // Fecha popover ao clicar fora
  useEffect(() => {
    if (!popoverOpen) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popoverOpen]);

  if (problematic.length === 0) return null;

  const channelNames = problematic
    .map((c) => c.displayName ?? c.displayNumber ?? `Canal #${c.id}`)
    .join(', ');

  return (
    <div ref={popoverRef} className="relative">
      <button
        type="button"
        onClick={() => setPopoverOpen((v) => !v)}
        onMouseEnter={() => setPopoverOpen(true)}
        className="relative flex h-[36px] w-[36px] items-center justify-center rounded-full transition"
        style={{ background: '#EF4444' }}
        aria-label={`${problematic.length} canal(is) com problema`}
        title="Canal com problema"
      >
        {/* Círculo interno branco com X vermelho dentro (2px de gap ao redor do X) */}
        <span className="flex h-[16px] w-[16px] items-center justify-center rounded-full bg-n-0">
          <X size={12} strokeWidth={3.5} style={{ color: '#EF4444' }} aria-hidden="true" />
        </span>
        {/* Pulse anel externo */}
        <span
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            animation: 'channel-health-pulse 1.6s ease-out infinite',
            boxShadow: '0 0 0 0 rgba(239,68,68,0.55)',
          }}
        />
      </button>

      <style>{`
        @keyframes channel-health-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(239,68,68,0.55); }
          70%  { box-shadow: 0 0 0 10px rgba(239,68,68,0); }
          100% { box-shadow: 0 0 0 0   rgba(239,68,68,0); }
        }
      `}</style>

      {popoverOpen && (
        <div
          className="absolute right-0 top-12 z-50 w-[320px] rounded-md border border-n-200 bg-n-0 px-3.5 py-3 text-left text-[12px] leading-relaxed text-n-700 shadow-lg"
          onMouseLeave={() => setPopoverOpen(false)}
        >
          Um ou mais canais não estão recebendo/enviando
          <br />
          mensagens. Reconecte para continuar.
          <br />
          {problematic.length > 1 ? 'Canais com problema' : 'Canal com problema'}:{' '}
          <strong className="text-n-900">{channelNames}</strong>
        </div>
      )}
    </div>
  );
}
